import {
    useDropzone,
} from 'react-dropzone'
import {
    useCallback,
    useEffect,
    useMemo,
    useState,
} from 'react'
import {
    CircularProgress,
} from '@mui/material'
import IconButton from '@mui/material/IconButton'
import DropZone from 'app/shared-components/DropZone/DropZone'
import Button from 'app/shared-components/Button'
import Label from 'app/shared-components/Label'
import {
    ValidationResult,
} from 'app/types/form.types'
import WarningCard from 'app/shared-components/WarningCard'

import useLabel from 'app/hooks/useLabel'
import useSharedStyles from '../../BatchActions.shared.styles'
import useParseCsvFile from './hooks/useParseCsvFile'
import useStyles from './CsvDataLoader.styles'
import validateCsv from './utils/validateCsv'

type Props = {
    onLoadData: (data: Record<string, any>[]) => void | Promise<void>,
    mandatoryHeaders?: string[],
    onCancel: () => void,
    onContinue: () => void,
    validateData?: (data: Record<string, any>[]) => ValidationResult,
}
const CsvDataLoader = ({
    onLoadData,
    mandatoryHeaders = [],
    onContinue,
    onCancel,
    validateData: additionalValidation,
}: Readonly<Props>) => {
    const l = useLabel()
    const {
        classes,
    } = useStyles()
    const {
        classes: sharedClasses,
    } = useSharedStyles()

    const [
        fileName,
        setFileName,
    ] = useState<string | null>(null)

    const [
        isLoading,
        setIsLoading,
    ] = useState(false)

    const [
        error,
        setError,
    ] = useState<string | null>(null)

    useEffect(() => {
        if (error) {
            setIsLoading(false)
        }
    }, [error])

    const handleLoadData = useCallback(async (data: Record<string, any>[], uploadedFile: File) => {
        const validationResult = validateCsv({
            data,
            additionalValidation,
            mandatoryHeaders,
            l,
        })

        if (validationResult === true) {
            try {
                await onLoadData(data)
                setFileName(uploadedFile.name)
            } catch (ex) {
                setError(ex.message || l('UNEXPECTED_ERROR_WHILE_LOADING_DATA'))
            }
        } else {
            setError(validationResult)
        }
        setIsLoading(false)
    }, [
        additionalValidation,
        l,
        mandatoryHeaders,
        onLoadData,
    ])

    const parseCsvFile = useParseCsvFile({
        onLoadData: handleLoadData,
        setError,
    })

    const handleUploadFile = useCallback(([selectedFile]: File[]) => {
        if (!selectedFile) {
            return
        }
        setIsLoading(true)
        setError(null)
        parseCsvFile(selectedFile)
    }, [parseCsvFile])

    const fileAccepted = useMemo(() => {
        return fileName !== null
    }, [fileName])

    const {
        getRootProps,
        getInputProps,
        open,
        isDragActive,
        isDragReject,
    } = useDropzone({
        onDrop: handleUploadFile,
        maxFiles: 1,
        accept: {
            'text/csv': [],
        },
        disabled: fileAccepted,
    })

    const handleRemoveFile = useCallback(() => {
        setFileName(null)
        setError(null)
    }, [])

    return (
        <div className={classes.container}>
            {error && (
                <WarningCard>
                    <span className={classes.bold}>IMPORTANT: </span>
                    {error}
                </WarningCard>
            )}
            <p className={classes.noticeText}>
                {isLoading
                    ? l('WAIT_UNTIL_FILE_RECOGNITION_COMPLETED')
                    : l('SELECT_CSV_FILE_TO_PROCEED')}

            </p>
            {isLoading ? (
                <div className={sharedClasses.loadingContainer}>
                    <CircularProgress />
                    {l('LOADING_RECORDS')}
                </div>
            ) : (
                <div className={classes.fileContainer}>
                    <Label
                        title={l('SOURCE_FILE')}
                        className={classes.fileInputLabel}
                    />
                    <DropZone
                        getRootProps={getRootProps}
                        getInputProps={getInputProps}
                        isDragActive={isDragActive}
                        isDragReject={isDragReject}
                        open={open}
                        disabled={fileAccepted}
                    />
                    {fileAccepted && (
                        <div className={classes.file}>
                            <span className={classes.fileName}>{fileName}</span>
                            <IconButton
                                onClick={handleRemoveFile}
                                className={classes.fileDeleteIcon}
                                data-testid="delete-file-button"
                            >
                                <img
                                    src="/assets/images/icons/delete.svg"
                                    alt="Delete file"
                                />
                            </IconButton>
                        </div>
                    )}
                </div>
            )}

            <div className={classes.buttonsContainer}>
                <Button
                    label={l('CANCEL')}
                    secondary
                    onClick={onCancel}
                />
                <Button
                    label={l('CONTINUE')}
                    onClick={onContinue}
                    disabled={!fileAccepted || isLoading}
                />
            </div>
        </div>
    )
}

export default CsvDataLoader
